import { useEffect } from 'react'
import { useNotesStore } from '../stores/notes-store'

/**
 * useNotes
 *
 * Thin façade over the notes store. It subscribes to individual slices (not the
 * whole store) so a consumer only re-renders when a field it actually uses
 * changes — a plain `useNotesStore()` re-rendered every page on any store write.
 * Action references are stable in Zustand, so selecting them is render-free.
 */
export function useNotes() {
  // State slices
  const notes = useNotesStore((s) => s.notes)
  const currentNote = useNotesStore((s) => s.currentNote)
  const isIndexLoading = useNotesStore((s) => s.isIndexLoading)
  const isContentLoading = useNotesStore((s) => s.isContentLoading)
  const indexError = useNotesStore((s) => s.indexError)
  const contentError = useNotesStore((s) => s.contentError)
  const searchQuery = useNotesStore((s) => s.searchQuery)
  const searchResults = useNotesStore((s) => s.searchResults)
  const searchTags = useNotesStore((s) => s.searchTags)
  const searchDomains = useNotesStore((s) => s.searchDomains)
  const lastSyncAt = useNotesStore((s) => s.lastSyncAt)
  const hasUnsavedChanges = useNotesStore((s) => s.hasUnsavedChanges)

  // Actions (stable references — no re-render on selection)
  const loadIndex = useNotesStore((s) => s.loadIndex)
  const rebuildIndex = useNotesStore((s) => s.rebuildIndex)
  const refreshIndex = useNotesStore((s) => s.refreshIndex)
  const openNote = useNotesStore((s) => s.openNote)
  const closeNote = useNotesStore((s) => s.closeNote)
  const saveNote = useNotesStore((s) => s.saveNote)
  const createNote = useNotesStore((s) => s.createNote)
  const deleteNote = useNotesStore((s) => s.deleteNote)
  const promoteToLongCourse = useNotesStore((s) => s.promoteToLongCourse)
  const setSearchQuery = useNotesStore((s) => s.setSearchQuery)
  const search = useNotesStore((s) => s.search)
  const getTagCloud = useNotesStore((s) => s.getTagCloud)
  const getDomainCloud = useNotesStore((s) => s.getDomainCloud)
  const setHasUnsavedChanges = useNotesStore((s) => s.setHasUnsavedChanges)

  // Initial index load. A failed load is NOT retried from here: without the
  // `indexError` guard a failure (no Google session, missing Drive scope)
  // flipped `isIndexLoading` back to false and re-ran this effect immediately —
  // a hot loop that froze the tab and hammered Drive with 403s. Retrying is
  // the job of an explicit action or of the sync engine.
  useEffect(() => {
    if (notes.length === 0 && !isIndexLoading && !lastSyncAt && !indexError) {
      void loadIndex()
    }
  }, [notes.length, isIndexLoading, lastSyncAt, indexError, loadIndex])

  return {
    // State
    notes,
    currentNote,
    isLoading: isIndexLoading || isContentLoading,
    error: indexError || contentError,

    // Search
    searchQuery,
    searchResults,
    searchTags,
    searchDomains,

    // Actions
    loadIndex,
    rebuildIndex,
    refreshIndex,
    openNote,
    closeNote,
    saveNote,
    createNote,
    deleteNote,
    promoteToLongCourse,
    setSearchQuery,
    search,
    getTagCloud,
    getDomainCloud,

    // Status
    lastSyncAt,
    hasUnsavedChanges,
    setHasUnsavedChanges,
  }
}
